import React, { useState, useEffect } from "react";
import "./OrdersTable.css";
import { FaEye, FaTrashAlt, FaFilePdf } from "react-icons/fa";
import { adminChangeOrderStatus, adminCancelOrder, adminGetSettings } from "../api/admin";
import html2pdf from 'html2pdf.js';

const OrdersTable = ({ orders = [], refreshOrders, onViewOrder }) => {
  const [updatingId, setUpdatingId] = useState(null);
  const [trackingPopup, setTrackingPopup] = useState(null);
  const [tempTracking, setTempTracking] = useState("");
  const [tempCourierName, setTempCourierName] = useState("");
  const [tempTrackingLink, setTempTrackingLink] = useState("");
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);

  const role = localStorage.getItem("adminRole");
  const permissions = JSON.parse(localStorage.getItem("adminPermissions") || "[]");

  const hasWritePermission = () => {
    if (role === "admin") return true;
    return permissions.includes("Orders:write");
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await adminGetSettings();
        setSiteSettings(res.data);
      } catch (err) {
        console.error("Failed to load settings in table", err);
      }
    };
    fetchSettings();
  }, []);

  const handleCancelOrder = async (orderId) => {
    if (!hasWritePermission()) return;
    if (!window.confirm("Are you sure you want to cancel this order?")) return;
    try {
      setUpdatingId(orderId);
      await adminCancelOrder(orderId);
      refreshOrders();
    } catch (err) {
      console.error("Cancel order failed", err);
      alert("Failed to cancel order");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    if (!hasWritePermission()) return;
    if (newStatus === "DISPATCHED") {
      setTrackingPopup({ orderId, status: newStatus });
      return;
    }

    try {
      setUpdatingId(orderId);
      await adminChangeOrderStatus(orderId, { status: newStatus });
      refreshOrders();
    } catch (err) {
      console.error("Status update failed", err);
      alert("Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  const submitTracking = async () => {
    if (!tempTracking.trim()) {
      alert("Please enter a tracking number");
      return;
    }

    try {
      setUpdatingId(trackingPopup.orderId);
      await adminChangeOrderStatus(trackingPopup.orderId, {
        status: trackingPopup.status,
        trackingNumber: tempTracking,
        courierName: tempCourierName,
        trackingLink: tempTrackingLink
      });
      setTrackingPopup(null);
      setTempTracking("");
      setTempCourierName("");
      setTempTrackingLink("");
      refreshOrders();
    } catch (err) {
      console.error("Tracking update failed", err);
      alert("Failed to update status with tracking");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleDownloadPDF = () => {
    const element = document.getElementById('orders-list-pdf-content');
    const opt = {
      margin: 0,
      filename: `Orders_List_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 3, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };
    html2pdf().from(element).set(opt).save().then(() => setShowPDFPreview(false));
  };

  return (
    <>
      <div className="orders-stats-container header-controls-row">
        <div className="admin-count-badge">
          Total Orders: {orders.length}
        </div>
        <button 
          className="btn-download-pdf"
          onClick={() => setShowPDFPreview(true)}
        >
          <FaFilePdf style={{ marginRight: '8px' }} />
          Download Receipt
        </button>
      </div>

      <div className="luxury-table-container">
        <table className="luxury-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Customer Name</th>
              <th>Order ID</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {orders.map((order) => (
              <tr key={order._id || order.id}>
                <td>
                  <div className="date-display" style={{ color: '#7e8299', fontSize: '0.9rem' }}>
                    {new Date(order.createdAt).toLocaleDateString(undefined, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </div>
                </td>
                <td className="customer-name-cell">
                  {order.shippingAddress?.fullName || order.shippingAddress?.name || order.user?.name || "Customer"}
                </td>
                <td>
                  <span className="order-id-badge" title={order._id}>
                    #{String(order._id || order.id).slice(-6).toUpperCase()}
                  </span>
                </td>
                <td className="price-tag">
                  ₹{(order.totalPrice || order.totalAmount || order.paymentResult?.amount || 0).toLocaleString()}
                </td>
                <td>
                   <div className="status-manage-cell">
                     <select
                       value={order.status}
                       onChange={(e) => handleStatusChange(order._id || order.id, e.target.value)}
                       disabled={updatingId === (order._id || order.id) || !hasWritePermission()}
                       className={`status-select ${(order.status || 'ORDER PLACED').toLowerCase().replace(/\s+/g, '-')}`}
                     >
                       <option value="ORDER PLACED">ORDER PLACED</option>
                       <option value="CONFIRMED">CONFIRMED</option>
                       <option value="DISPATCHED">DISPATCHED</option>
                       <option value="DELIVERED">DELIVERED</option>
                       <option value="CANCELED">CANCELED</option>
                       <option value="RETURN REQUESTED">RETURN REQUESTED</option>
                       <option value="RETURN APPROVED">RETURN APPROVED</option>
                       <option value="RETURN REJECTED">RETURN REJECTED</option>
                       <option value="PICKED UP">PICKED UP</option>
                       <option value="REFUNDED">REFUNDED</option>
                     </select>
                     {updatingId === (order._id || order.id) && <div className="updating-spinner"></div>}
                   </div>
                </td>
                <td>
                  <div className="table-actions">
                    <button
                      className="action-btn view"
                      onClick={() => onViewOrder(order)}
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                    {hasWritePermission() && order.status !== "CANCELED" && order.status !== "DELIVERED" && (
                      <button
                        className="action-btn delete"
                        onClick={() => handleCancelOrder(order._id || order.id)}
                        title="Cancel Order"
                      >
                        <FaTrashAlt />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* PDF Preview Modal for Orders List */}
      {showPDFPreview && (
        <div className="modal-overlay pdf-preview-modal" style={{ zIndex: 3000 }}>
          <div className="modal-content" style={{ maxWidth: '1000px', backgroundColor: '#f0f2f5', height: '90vh' }}>
            <div className="modal-header">
              <h3>PDF Preview - Orders List</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn-save" onClick={handleDownloadPDF}>Download PDF</button>
                <button className="btn-cancel" onClick={() => setShowPDFPreview(false)}>Close</button>
              </div>
            </div>
            <div className="form-body pdf-scroll-wrapper" style={{ padding: 0 }}>
              <div id="orders-list-pdf-content" className="pdf-preview-content" style={{ 
                backgroundColor: 'white', 
                padding: '12mm', 
                width: '210mm',
                minHeight: '296mm',
                boxSizing: 'border-box',
                position: 'relative',
                fontFamily: "'Outfit', sans-serif"
              }}>
                <div style={{ textAlign: 'center', marginBottom: '30px', borderBottom: '2px solid #181c32', paddingBottom: '20px' }}>
                  <h1 style={{ margin: '0 0 5px 0', fontSize: '24px', fontWeight: '800', color: '#181c32' }}>
                    {siteSettings?.site_name || "ORDERS LIST"}
                  </h1>
                  <p style={{ margin: '0 0 5px 0', color: '#3f4254', fontSize: '13px', fontWeight: '500' }}>
                    {siteSettings?.address || "Premium Culinary Experience"}
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', fontSize: '12px', color: '#7e8299' }}>
                    <span>Email: {siteSettings?.contact_email}</span>
                    <span>Phone: {siteSettings?.contact_phone}</span>
                  </div>
                  <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '1px dashed #eff2f5' }}>
                    <p style={{ margin: 0, color: '#181c32', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Orders Report Overview
                    </p>
                    <p style={{ margin: 0, color: '#a1a5b7', fontSize: '11px' }}>
                      Total Orders: {orders.length} | Generated on: {new Date().toLocaleDateString('en-GB')}
                    </p>
                  </div>
                </div>
                
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #eff2f5' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #eff2f5' }}>
                      <th style={{ padding: '15px', textAlign: 'left', fontSize: '11px', fontWeight: '800', fontFamily: "'Playfair Display', serif", color: '#181c32', textTransform: 'uppercase', letterSpacing: '1px' }}>Date</th>
                      <th style={{ padding: '15px', textAlign: 'left', fontSize: '11px', fontWeight: '800', fontFamily: "'Playfair Display', serif", color: '#181c32', textTransform: 'uppercase', letterSpacing: '1px' }}>Customer</th>
                      <th style={{ padding: '15px', textAlign: 'left', fontSize: '11px', fontWeight: '800', fontFamily: "'Playfair Display', serif", color: '#181c32', textTransform: 'uppercase', letterSpacing: '1px' }}>Order ID</th>
                      <th style={{ padding: '15px', textAlign: 'right', fontSize: '11px', fontWeight: '800', fontFamily: "'Playfair Display', serif", color: '#181c32', textTransform: 'uppercase', letterSpacing: '1px' }}>Amount</th>
                      <th style={{ padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '800', fontFamily: "'Playfair Display', serif", color: '#181c32', textTransform: 'uppercase', letterSpacing: '1px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order._id || order.id} style={{ borderBottom: '1px solid #eff2f5', pageBreakInside: 'avoid' }}>
                        <td style={{ padding: '12px 15px', fontSize: '12px', color: '#3f4254' }}>
                          {new Date(order.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                        </td>
                        <td style={{ padding: '12px 15px', fontSize: '13px', fontWeight: '700', color: '#181c32' }}>
                          {order.shippingAddress?.fullName || order.shippingAddress?.name || order.user?.name || "Customer"}
                        </td>
                        <td style={{ padding: '12px 15px', fontSize: '12px', color: '#3f4254' }}>
                          #{String(order._id || order.id).slice(-6).toUpperCase()}
                        </td>
                        <td style={{ padding: '15px', textAlign: 'right', fontSize: '13px', fontWeight: '800', color: '#181c32', fontFamily: "'Outfit', sans-serif" }}>
                          ₹{(order.totalPrice || order.totalAmount || 0).toLocaleString()}
                        </td>
                        <td style={{ padding: '12px 15px', textAlign: 'center' }}>
                          <span style={{ 
                            fontSize: '9px', 
                            fontWeight: '800',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: 
                              order.status === 'DELIVERED' ? '#e8fff3' :
                              order.status === 'CANCELED' ? '#fff5f8' :
                              order.status === 'DISPATCHED' ? '#f1faff' :
                              order.status === 'CONFIRMED' ? '#e1f0ff' : '#fff8dd',
                            color: 
                              order.status === 'DELIVERED' ? '#50cd89' :
                              order.status === 'CANCELED' ? '#f1416c' :
                              order.status === 'DISPATCHED' ? '#0095e8' :
                              order.status === 'CONFIRMED' ? '#0095e8' : '#ffc700'
                          }}>{order.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: '30px', textAlign: 'right', fontSize: '12px', color: '#a1a5b7' }}>
                  Generated by Nbitez Admin Panel
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tracking Number Popup */} 
      {trackingPopup && (
        <div className="modal-overlay" style={{ zIndex: 2000 }}>
          <div className="modal-content" style={{ maxWidth: '450px', padding: '0' }}>
            <div className="modal-header" style={{ padding: '10px 15px' }}>
              <h3 style={{ fontSize: '1.1rem' }}>Enter Tracking Details</h3>
              <button className="close-btn" onClick={() => setTrackingPopup(null)}>&times;</button>
            </div>
            <div className="form-body" style={{ padding: '0px 10px' }}>
              <p style={{ marginTop: '0', marginBottom: '6px', color: '#7e8299' }}>
                Please provide the tracking number for Order <b>#{String(trackingPopup.orderId).slice(-6).toUpperCase()}</b>
              </p>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '13px' }}>
                Tracking Number
              </label>
              <input
                type="text"
                className="tracking-input"
                placeholder="e.g. TRACK123456"
                value={tempTracking}
                onChange={(e) => setTempTracking(e.target.value)}
                autoFocus
              />
              
              <label style={{ display: 'block', marginBottom: '2px', marginTop: '5px', fontWeight: '600', fontSize: '13px' }}>
                Courier Name
              </label>
              <input
                type="text"
                className="tracking-input"
                placeholder="ST Courier"
                value={tempCourierName}
                onChange={(e) => setTempCourierName(e.target.value)}
              />
              
              <label style={{ display: 'block', marginBottom: '2px', marginTop: '5px', fontWeight: '600', fontSize: '13px' }}>
                Tracking Link
              </label>
              <input
                type="url"
                className="tracking-input"
                placeholder="https://stcourier.com/"
                value={tempTrackingLink}
                onChange={(e) => setTempTrackingLink(e.target.value)}
              />
            </div>
            <div className="form-footer" style={{ padding: '5px 10px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn-cancel" onClick={() => setTrackingPopup(null)}>Cancel</button>
              <button className="btn-save" onClick={submitTracking}>Update Status</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default OrdersTable;
